import type { XhrStatus } from 'redux/reusable/xhrStatus';
import type { FetchStatus } from 'redux/state';
import type {
  BudgetRecurrence,
  LightProject,
  Project,
  Draft as ProjectDraft,
  ProjectUserRelation,
  ProjectVariant,
} from 'types/project';
import type { Cost, Duration } from 'types/units';
import { actionCreatorFactory } from 'typescript-fsa';

const actionCreator = actionCreatorFactory('timely/projects');

export const createDraft = actionCreator<ProjectDraft>('CREATE_DRAFT');
export const clearDraft = actionCreator('CLEAR_DRAFT');
export const setDraftOriginal = actionCreator<Project>('CREATE_ORIGINAL_DRAFT');
export const clearOriginalDraft = actionCreator('CLEAR_ORIGINAL_DRAFT');
export const updateDraft = actionCreator<Partial<ProjectDraft> | null>('UPDATE_DRAFT');
export const tagListAdd = actionCreator<{
  id: number;
  default?: boolean;
}>('TAG_LIST_ADD');
export const tagListRemove = actionCreator<{
  id: number;
  children: Array<number>;
}>('TAG_LIST_REMOVE');
export const tagAdd = actionCreator<{
  id: number;
}>('TAG_ADD');
export const tagRemove = actionCreator<{
  id: number;
}>('TAG_REMOVE');
export const tagAddBatch = actionCreator<{
  ids: Array<number>;
  default?: boolean;
}>('TAG_ADD_BATCH');
export const tagListChangeRequired = actionCreator<{
  id: number;
  required: boolean;
}>('TAG_LIST_CHANGE_REQUIRED');
export const tagListRemoveBatch = actionCreator('TAG_LIST_REMOVE_BATCH');
export const tagChangeBudget = actionCreator<{
  id: number;
  budget: number;
}>('TAG_CHANGE_BUDGET');
export const tagListChangeDefault = actionCreator<{
  tagListId: number;
  tagId?: number;
}>('TAG_LIST_CHANGE_DEFAULT');
export const tagRemoveBatch = actionCreator<Array<number>>('TAG_REMOVE_BATCH');
export const updateDraftBudgetRecurrence = actionCreator<Partial<BudgetRecurrence>>(
  'UPDATE_DRAFT_BUDGET_RECURRENCE'
);
export const updateDraftBudgetRecurrenceStart = actionCreator<string>(
  'UPDATE_DRAFT_BUDGET_RECURRENCE_START'
);
export const updateDraftBudgetRecurrenceEnd = actionCreator<string | null>(
  'UPDATE_DRAFT_BUDGET_RECURRENCE_END'
);
export const setProjectBudgetAsPending = actionCreator<number>('SET_PROJECT_BUDGET_AS_PENDING');
export const projectSaved = actionCreator<{
  project: Project;
  createdEntry: boolean;
}>('SAVED');
export const fetchedFullProjects = actionCreator<Array<Project>>('FETCHED_FULL_PROJECTS');
export const setProjectFetchingStatus = actionCreator<ProjectFetchingStatus>(
  'SET_PROJECT_FETCHING_STATUS'
);
export const fetchedLightProjects = actionCreator<{
  projects: Array<LightProject>;
}>('FETCHED_LIGHT_PROJECTS');
export const setFilter = actionCreator<StateFilter>('SET_FILTER');
export const removeProject = actionCreator<number>('REMOVE_PROJECT');
export const activateProject = actionCreator<number>('ACTIVATE_PROJECT');
export const archiveProject = actionCreator<number>('ARCHIVE_PROJECT');
export const archiveProjectsForClient = actionCreator<Record<number, ProjectVariant | undefined>>(
  'ARCHIVE_PROJECTS_ON_CLIENT_ID'
);
export const fetchedBudgets = actionCreator<{
  budgets: Array<BudgetEntity>;
  projectId: number;
}>('FETCHED_BUDGETS');
export const setProjectPermissions = actionCreator<{
  userId: number;
  permissions: Array<number>;
}>('SET_PROJECT_PERMISSIONS');
export const touchProjectPermission = actionCreator<{
  userId: number;
  projectId: number;
}>('TOUCH_PROJECT_PERMISSION');
export const replaceProjectPermissionForAll = actionCreator<{
  projectId: number;
  /** If `userIds` aren't passed, permissions will be removed instead of replaced. */
  userIds?: Array<number>;
}>('REPLACE_PROJECT_PERMISSION');
export const updateProjectUserAssociation = actionCreator<{
  userId: number;
  changes: Partial<ProjectUserRelation>;
}>('UPDATE_PROJECT_USER_ASSOCIATION');
export const removeUserIdFromDraft = actionCreator<number>('REMOVE_USER_ID_FROM_DRAFT');

export const setPaginatedProjectView = actionCreator<PaginatedProjectView | null>(
  'SET_PAGINATED_PROJECT_VIEW'
);

export interface FilterProjectSortBy {
  field: FilterProjectSortByFields;
  direction: 'asc' | 'desc';
}
export type FilterProjectSortByFields = 'name' | 'client_name' | 'budget' | 'budget_percent';
// | 'budget_remaining';
// | 'hours'
// Too expensive to sort by hours on server-side
// Ref https://memoryai.slack.com/archives/C04AT0ZPZ/p1594036044245800

export type FilterProjectStatus = 'active' | 'archived' | 'all';
export type FilterProjectRelation = 'created' | 'assigned' | 'all';
export type FilterBudgetStatus = 'all' | 'billable' | 'non-billable';

export interface StateFilter {
  sortBy: FilterProjectSortBy;
  projectStatus: FilterProjectStatus;
  projectRelation: FilterProjectRelation;
  budgetStatus: FilterBudgetStatus;
  labelIds: Array<number>;
  clientIds: Array<number>;
  query: string;
}

export interface BudgetEntity {
  recur: string;
  start_date: string;
  end_date: string;
  key?: string;
  totals: {
    duration: Duration;
    estimated_duration: Duration;
    cost: Cost;
    estimated_cost: Cost;
  };
}

export interface PaginatedProjectView {
  page: number;
  projectIds: Array<number>;
  totalCount?: number;
}

export type ProjectFetchingStatus =
  | 'lazy'
  | 'fetchedInitial'
  | 'fetchedAllLight'
  | 'fetchedAllFull'
  | 'fetchedAllActiveFull'
  | 'fetchedAllActiveLight';

export interface State {
  draft: ProjectDraft | null;
  draftOriginal: Project | null;
  projectPermissionsByUserId: Record<number, Array<number>>;
  projectById: Record<number, ProjectVariant | undefined>;
  projectIds: Array<number>;
  projectFetchingStatus: ProjectFetchingStatus;
  budgets: {
    entities: Record<number, Record<string, BudgetEntity>>;
    intervalsByProjectId: Record<number, Array<string>>;
    fetchStatus: Record<number, FetchStatus>;
  };

  paginatedView: null | PaginatedProjectView;
  filter: StateFilter;
  xhrStatus: XhrStatus;
}
